import { useRouter, useRoute } from 'vue-router';
import navigationService from '@/utils/navigation-service';

/**
 * Composable for consistent navigation behavior across the app
 * Provides standardized back navigation with intelligent fallbacks
 */
export function useNavigation() {
  const router = useRouter();
  const route = useRoute();

  /**
   * Navigate back to the previous screen
   * Uses the following priority:
   * 1. Custom back handler (if registered via navigationService)
   * 2. Browser history back (if available) - STACK-BASED
   * 3. Fallback route (for deep links, notifications, direct URLs)
   *
   * @param {string|object} fallback - Route to navigate to if no history
   *                                    Can be string path or route object
   *                                    Default: '/home'
   * @example
   * // Simple usage with default fallback
   * goBack(); // Uses '/home' as fallback
   *
   * // Custom fallback
   * goBack('/library');
   *
   * // Route object fallback
   * goBack({ name: 'MyGiving', params: { id: 123 } });
   */
  const goBack = (fallback = '/home') => {
    // Priority 1: Use custom handler if registered
    if (navigationService.hasCustomHandler()) {
      navigationService.goBack(router);
      return;
    }

    // Priority 2: Use browser history if available (STACK-BASED)
    if (window.history.length > 1) {
      router.back();
      return;
    }

    // Priority 3: Navigate to fallback route
    // Handle both string paths and route objects
    if (typeof fallback === 'string') {
      router.push(fallback);
    } else {
      router.push(fallback);
    }
  };

  /**
   * Check if back navigation is possible
   * @returns {boolean}
   */
  const canGoBack = () => {
    return window.history.length > 1 || navigationService.hasCustomHandler();
  };

  /**
   * Navigate to a specific route
   * @param {string|object} destination - Route path or route object
   */
  const goTo = destination => {
    router.push(destination);
  };

  /**
   * Navigate to home screen
   */
  const goHome = () => {
    router.push('/home');
  };

  /**
   * Replace current route (no history entry)
   * @param {string|object} destination - Route path or route object
   */
  const replaceTo = destination => {
    router.replace(destination);
  };

  /**
   * Get current route path
   * @returns {string}
   */
  const currentPath = () => {
    return route.path;
  };

  /**
   * Get current route name
   * @returns {string}
   */
  const currentRouteName = () => {
    return route.name;
  };

  /**
   * Register a custom back handler for the current page
   * Useful for pages with internal navigation (e.g., multi-step forms)
   * The handler will be automatically cleared when leaving the page
   *
   * @param {Function} handler - Custom back navigation function
   * @example
   * // In a multi-step form
   * const { registerBackHandler, clearBackHandler } = useNavigation();
   *
   * onMounted(() => {
   *   registerBackHandler(() => {
   *     if (currentStep.value > 1) {
   *       currentStep.value--;
   *     } else {
   *       clearBackHandler();
   *       router.back();
   *     }
   *   });
   * });
   *
   * onBeforeUnmount(() => {
   *   clearBackHandler();
   * });
   */
  const registerBackHandler = handler => {
    navigationService.registerBackHandler(handler, route.path);
  };

  /**
   * Clear the custom back handler
   * Should be called in onBeforeUnmount or when custom behavior is no longer needed
   */
  const clearBackHandler = () => {
    navigationService.clearBackHandler();
  };

  return {
    // Navigation methods
    goBack,
    canGoBack,
    goTo,
    goHome,
    replaceTo,

    // Route info
    currentPath,
    currentRouteName,

    // Custom handlers
    registerBackHandler,
    clearBackHandler,
  };
}
